/**
 * Form Name Generator Utility
 * Generates meaningful internal names for forms used in automations and QR generation
 */

/**
 * Generate form name for property forms based on address
 * @param {Object} property - Property object with address
 * @returns {string} - Generated form name
 */
export const generatePropertyFormName = (property) => {
    const address = property?.address || 'Property';
    return `Lead Form - ${address}`;
};

/**
 * Generate form name for portfolio forms with index
 * @param {number} index - Index of the form in the portfolio
 * @param {string} fallbackName - Optional fallback name
 * @returns {string} - Generated form name
 */
export const generatePortfolioFormName = (index, fallbackName = null) => {
    if (fallbackName && fallbackName.trim()) {
        return `Portfolio Form - ${fallbackName}`;
    }
    return `Portfolio Form ${index + 1}`;
};

/**
 * Generate unique ID for a form (used for internal tracking)
 * @returns {string} - Unique form ID
 */
export const generateFormId = () => {
    return `form-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Ensure form fields have formName and formId
 * Used during migration and updates to maintain backward compatibility
 * @param {Array} formFields - Array of form field objects
 * @param {string} propertyAddress - Property address (for property forms)
 * @returns {Array} - Form fields with formName and formId
 */
export const ensureFormFieldsHaveNames = (formFields = [], propertyAddress = '') => {
    if (!Array.isArray(formFields)) return [];

    const baseFormName = propertyAddress ? `Lead Form - ${propertyAddress}` : 'Lead Form';

    return formFields.map((field, index) => {
        // Only set formName on the form itself (not per-field), so use index 0
        if (index === 0 && !field.formName && !field.formId) {
            return {
                ...field,
                formName: baseFormName,
                formId: generateFormId(),
            };
        }
        return field;
    });
};

/**
 * Ensure portfolio fields have formName and formId
 * @param {Array} customFields - Array of portfolio field names/values
 * @returns {Array} - Fields with internal form metadata
 */
export const ensurePortfolioFieldsHaveNames = (customFields = []) => {
    if (!Array.isArray(customFields)) return [];

    return customFields.map((field, index) => {
        // Check if field is already an object with formName
        if (typeof field === 'object' && field !== null) {
            if (!field.formId) {
                return {
                    ...field,
                    formId: generateFormId(),
                };
            }
            return field;
        }

        // If it's a string, convert to object with metadata
        return {
            value: field,
            formName: generatePortfolioFormName(index, field),
            formId: generateFormId(),
        };
    });
};

export default {
    generatePropertyFormName,
    generatePortfolioFormName,
    generateFormId,
    ensureFormFieldsHaveNames,
    ensurePortfolioFieldsHaveNames,
};
